import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import { Users, Bell, Heart } from 'lucide-react';

/* ─── Pie: Status das Familias ─── */
const FAM_COLORS = ['#10b981', '#f59e0b', '#6366f1', '#ef4444'];

export function FamiliasPieChart({ familias }: { familias: any }) {
  if (!familias) return <div className="muni-empty"><p>Sem dados</p></div>;

  const data = [
    { name: 'Contas Ativas', value: familias.comContaAtiva || 0 },
    { name: 'Pendentes', value: familias.comContaPendente || 0 },
    { name: 'Sem Conta', value: familias.semConta || 0 },
  ].filter(d => d.value > 0);

  if (data.length === 0) return <div className="muni-empty"><p>Sem dados</p></div>;

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="muni-chart-card">
      <h4 className="muni-chart-title"><Users size={13} style={{ color: '#1e40af' }} /> Status das Familias</h4>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ResponsiveContainer width={100} height={100}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={24} outerRadius={42} paddingAngle={3} dataKey="value">
              {data.map((_, i) => <Cell key={i} fill={FAM_COLORS[i % FAM_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: any) => `${v} (${total > 0 ? Math.round((v / total) * 100) : 0}%)`} />
          </PieChart>
        </ResponsiveContainer>
        <div className="muni-chart-legend">
          {data.map((d, i) => (
            <div key={d.name} className="muni-chart-legend-item">
              <span className="muni-chart-legend-dot" style={{ background: FAM_COLORS[i % FAM_COLORS.length] }} />
              <span className="muni-chart-legend-label">{d.name}</span>
              <strong className="muni-chart-legend-value">{d.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Area: Notificacoes da Prefeitura aos Pais (30 dias) ─── */
export function NotificacoesFamiliasChart({ notificacoes }: { notificacoes: any }) {
  // Dados reais do resumo de notificações
  const total = notificacoes?.total || 0;
  const entregues = notificacoes?.entregues || 0;
  const lidas = notificacoes?.lidas || 0;
  const pendentes = notificacoes?.pendentes || 0;
  const falhas = notificacoes?.falhas || 0;

  const barData = [
    { name: 'Disparadas', valor: total, fill: '#6366f1' },
    { name: 'Entregues', valor: entregues, fill: '#10b981' },
    { name: 'Lidas', valor: lidas, fill: '#059669' },
    { name: 'Pendentes', valor: pendentes, fill: '#f59e0b' },
    { name: 'Falhas', valor: falhas, fill: '#ef4444' },
  ];

  return (
    <div className="muni-chart-card">
      <h4 className="muni-chart-title"><Bell size={13} style={{ color: '#6366f1' }} /> Comunicacao c/ Pais</h4>
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={barData} margin={{ top: 2, right: 5, left: -25, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
          <XAxis dataKey="name" tick={{ fontSize: 8, fill: 'var(--text-muted, #9ca3af)' }} />
          <YAxis tick={{ fontSize: 8, fill: 'var(--text-muted, #9ca3af)' }} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Bar dataKey="valor" radius={[3, 3, 0, 0]}>
            {barData.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="muni-chart-footer-row">
        <span>Taxa leitura: <strong style={{ color: '#059669' }}>{notificacoes?.taxaLeitura || 0}%</strong></span>
      </div>
    </div>
  );
}

/* ─── Mini: Cobertura e Alcance ─── */
export function CoberturaAlcanceChart({ familias }: { familias: any }) {
  if (!familias) return null;

  const metricas = [
    { label: 'Cobertura', pct: familias.taxaCobertura || 0, cor: '#6366f1' },
    { label: 'Ativacao', pct: familias.taxaAtivacao || 0, cor: '#10b981' },
  ];

  const alcanceTotal = familias.totalResponsaveis > 0
    ? Math.round(((familias.comContaAtiva + familias.comContaPendente) / familias.totalResponsaveis) * 100)
    : 0;

  return (
    <div className="muni-chart-card">
      <h4 className="muni-chart-title"><Heart size={13} style={{ color: '#e11d48' }} /> Alcance Politico</h4>
      <div className="muni-alcance-mini-big">
        <span className="muni-alcance-mini-pct">{alcanceTotal}%</span>
        <span className="muni-alcance-mini-label">familias alcancadas</span>
      </div>
      <div className="muni-alcance-mini-bars">
        {metricas.map(m => (
          <div key={m.label} className="muni-alcance-mini-bar-row">
            <span className="muni-alcance-mini-bar-label">{m.label}</span>
            <div className="muni-alcance-mini-bar-track">
              <div className="muni-alcance-mini-bar-fill" style={{ width: `${Math.min(m.pct, 100)}%`, background: m.cor }} />
            </div>
            <strong style={{ color: m.cor, fontSize: '.65rem' }}>{m.pct}%</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
